import { useState, useEffect } from "react";
import { X, Upload, Loader2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const FIELD_LABELS = {
  name: "Nazwa / Abonent", address: "Adres (pełny)", street: "Ulica i nr", postal_code: "Kod pocztowy", city: "Miejscowość",
  phone: "Telefon", email: "E-mail", onu_sn: "Numer seryjny ONU", lat: "Szerokość (lat)", lng: "Długość (lng)",
  plan: "Pakiet / Taryfa", notes: "Uwagi",
};

export default function ClientImportDialog({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null); // {columns, fields, suggested_mapping, sample, total}
  const [mapping, setMapping] = useState({});
  const [teryt, setTeryt] = useState(null); // {has_teryt_base, results:[{matched,city,street,nr}]}

  const inspect = async (f) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("dry_run", "true");
      const { data } = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMeta(data);
      setMapping(data.suggested_mapping || {});
    } catch (e) { toast.error(e.response?.data?.detail || "Nie udało się odczytać pliku"); }
    finally { setBusy(false); }
  };

  // Podgląd dopasowania do słownika TERYT dla próbki (odświeżany przy zmianie mapowania).
  useEffect(() => {
    if (!meta?.sample?.length) { setTeryt(null); return; }
    if (!mapping.city && !mapping.street && !mapping.address) { setTeryt(null); return; }
    let cancelled = false;
    const items = meta.sample.map((row) => ({
      city: mapping.city ? String(row[mapping.city] ?? "") : "",
      street: mapping.street ? String(row[mapping.street] ?? "") : "",
      address: mapping.address ? String(row[mapping.address] ?? "") : "",
    }));
    api.post("/clients/teryt-match-preview", { items })
      .then((r) => { if (!cancelled) setTeryt(r.data); })
      .catch(() => { if (!cancelled) setTeryt(null); });
    return () => { cancelled = true; };
  }, [meta, mapping]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setMeta(null); setMapping({});
    inspect(f);
  };

  const commit = async () => {
    if (!file) return;
    if (!Object.values(mapping).includes(mapping.name) || !mapping.name) { toast.error("Zmapuj pole „Nazwa / Abonent”"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      const { data } = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Zaimportowano ${data.created} klientów (pominięto ${data.skipped})`);
      onImported && onImported();
      onClose && onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Import nieudany"); }
    finally { setBusy(false); }
  };

  const setField = (field, col) => setMapping((m) => ({ ...m, [field]: col || undefined }));

  return (
    <div className="fixed inset-0 z-[760] bg-black/60 flex items-center justify-center p-4" data-testid="client-import-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2"><FileSpreadsheet size={16} className="text-cyan-400" /> Import klientów (CSV / XML)</h3>
          <button onClick={onClose} data-testid="client-import-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        <label className="flex items-center gap-2 text-sm text-cyan-300 hover:text-cyan-200 cursor-pointer border border-dashed border-slate-600 rounded-lg px-3 py-4 justify-center">
          <Upload size={16} /> {file ? file.name : "Wybierz plik CSV lub XML"}
          <input type="file" accept=".csv,.xml,text/csv,text/xml,application/xml" className="hidden" onChange={onFile} data-testid="client-import-file" />
        </label>

        {busy && !meta && <div className="mt-3 text-xs text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Analizuję plik…</div>}

        {meta && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Dopasowanie pól ({meta.total} rekordów) — możesz ustawić ręcznie</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="client-import-mapping">
              {meta.fields.map((field) => (
                <div key={field} className="flex items-center gap-2">
                  <span className="text-xs text-slate-300 w-32 shrink-0">{FIELD_LABELS[field] || field}{field === "name" && <span className="text-rose-400">*</span>}</span>
                  <select value={mapping[field] || ""} onChange={(e) => setField(field, e.target.value)} data-testid={`client-map-${field}`}
                    className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-cyan-500">
                    <option value="">— pomiń —</option>
                    {meta.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>
            {meta.sample?.length > 0 && (() => {
              const mapped = meta.fields.filter((f) => mapping[f]);
              return (
                <div className="mt-3" data-testid="client-import-preview">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Podgląd (pierwsze {meta.sample.length}) — z zastosowanym mapowaniem</div>
                  <div className="overflow-x-auto border border-slate-800 rounded">
                    <table className="text-[11px] w-full">
                      <thead><tr className="bg-slate-800/60 text-slate-400">
                        {mapped.map((f) => <th key={f} className="px-2 py-1 text-left whitespace-nowrap">{FIELD_LABELS[f] || f}</th>)}
                        <th className="px-2 py-1 text-left whitespace-nowrap">TERYT (dopasowanie)</th>
                      </tr></thead>
                      <tbody>
                        {meta.sample.map((row, i) => {
                          const m = teryt?.results?.[i];
                          return (
                          <tr key={i} className="border-t border-slate-800" data-testid="client-import-preview-row">
                            {mapped.map((f) => <td key={f} className="px-2 py-1 text-slate-300 whitespace-nowrap">{mapping[f] ? String(row[mapping[f]] ?? "") : ""}</td>)}
                            <td className="px-2 py-1 whitespace-nowrap" data-testid={`client-import-teryt-${i}`}>
                              {!teryt ? <span className="text-slate-600">…</span> : m?.matched ? (
                                <span className="text-emerald-400">✓ {[m.city, m.street, m.nr].filter(Boolean).join(", ")}</span>
                              ) : (
                                <span className="text-amber-400">✕ brak dopasowania</span>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {teryt && !teryt.has_teryt_base && (
                    <div className="text-[10px] text-rose-300 mt-1">Brak wgranej bazy TERYT — wgraj słownik GUS w module UKE, aby dopasować adresy.</div>
                  )}
                  <div className="text-[10px] text-slate-500 mt-1">Adresy zostaną dopasowane do wewnętrznej bazy TERYT (kody TERC/SIMC/ULIC) w tle. Niedopasowane trafią do weryfikacji w Pulpicie NOC.</div>
                </div>
              );
            })()}
            <button onClick={commit} disabled={busy} data-testid="client-import-commit"
              className="mt-4 w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded px-3 py-2 text-sm flex items-center justify-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Importuj {meta.total} klientów
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
